import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import minusIcon from '@/assets/icons/minus.svg';
import addIcon from '@/assets/icons/add.svg';
import PersonFields from './PersonFields';
import { FileField } from './FileField';
import AdditionalDocumentsField from './AdditionalDocumentsField';
import { useEditMode } from '../EditModeContext';
import { useOpenFundFieldTracking } from '../OpenFundAnalyticsContext';
import {
  createDefaultSupported,
  MAX_WELFARE_SUPPORTEDS,
  WelfareFormData,
} from '../schemas/openFundFormSchema';
import { RelationshipOptions } from '@/models/common/RelationshipOptions';
import styles from './ArrayFields.module.scss';
import fieldStyles from './FormField.module.scss';

const NON_SELF_RELATIONSHIPS = Object.values(RelationshipOptions).filter(
  (option) => option !== RelationshipOptions.Self
);

const SupportedFields = () => {
  const { t } = useTranslation();
  const isEditMode = useEditMode();
  const { control, setValue } = useFormContext<WelfareFormData>();
  const trackField = useOpenFundFieldTracking();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'supporteds',
  });

  const canAdd = !isEditMode && fields.length < MAX_WELFARE_SUPPORTEDS;

  const handleRemove = (index: number) => {
    remove(index);
    setValue(
      `supporteds.0.relationshipToFirstSupported`,
      RelationshipOptions.Self,
      { shouldValidate: false, shouldDirty: false }
    );
  };

  return (
    <div className={styles.wrapper}>
      {fields.map((field, index) => (
        <div className={styles.entry} key={field.id}>
          <div className={styles.entryHeader}>
            <span className="body-lr-semibold text-(--font-heading)">
              {t('openFundRequest.fields.supportedDetails')}{fields.length > 1 ? ` ${index + 1}` : ''}
            </span>
            {!isEditMode && fields.length > 1 && (
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => handleRemove(index)}
                aria-label={t('openFundRequest.buttons.removeSupported')}
              >
                <img src={minusIcon} alt="" />
              </button>
            )}
          </div>
          <PersonFields namePrefix={`supporteds.${index}`} />
          <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
            <FileField
              name={`supporteds.${index}.idCardPhotocopy`}
              label={t('openFundRequest.fields.idCardPhotocopy')}
            />
            <FileField
              name={`supporteds.${index}.idAppendixPhotocopy`}
              label={t('openFundRequest.fields.idAppendixPhotocopy')}
            />
          </div>
          <AdditionalDocumentsField namePrefix={`supporteds.${index}.additionalDocuments`} />
          {index > 0 && (
            <div className={fieldStyles.fieldWrapper}>
              <label className={`body-sm-regular ${fieldStyles.label}`}>
                {t('openFundRequest.fields.relationship')}
              </label>
              <Controller
                control={control}
                name={`supporteds.${index}.relationshipToFirstSupported`}
                render={({ field }) => {
                  const fieldName = `supporteds.${index}.relationshipToFirstSupported`;
                  return (
                    <select
                      {...field}
                      onFocus={() => trackField(fieldName)}
                      onChange={(event) => {
                        field.onChange(event);
                        trackField(fieldName);
                      }}
                      className={`${fieldStyles.input} body-md-regular`}
                    >
                    {NON_SELF_RELATIONSHIPS.map((option) => (
                      <option key={option} value={option}>
                        {t(`openFundRequest.relationships.${option}`)}
                      </option>
                    ))}
                    </select>
                  );
                }}
              />
            </div>
          )}
        </div>
      ))}
      {canAdd && (
        <>
          <span className={`body-sm-regular ${styles.note}`}>
            {t('openFundRequest.notes.addSupported')}
          </span>
          <button
            type="button"
            className={styles.addButton}
            onClick={() => append(createDefaultSupported(false))}
          >
            <img src={addIcon} alt="" />
            {t('openFundRequest.buttons.addSupported')}
          </button>
        </>
      )}
    </div>
  );
};

export default SupportedFields;
